import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  TrendUp, ChartLineUp, Target, Coins, ShieldCheck,
  Clock, ArrowsHorizontal, Pulse, CircleNotch, ShareNetwork,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import ShareCardModal from "./ShareCardModal";
import ShareTrackRecordCard from "./ShareTrackRecordCard";

/** Stat card — one KPI tile. */
function Kpi({ label, value, suffix, icon: Icon, tone, hint, testid }) {
  return (
    <div
      className={
        "border rounded-sm p-3 bg-surface/60 " +
        (tone === "pos" ? "border-pos/40" : tone === "neg" ? "border-neg/40" : "border-subtle")
      }
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        {Icon && <Icon size={11} weight="fill" className={
          tone === "pos" ? "text-pos" : tone === "neg" ? "text-neg" : "text-mod"
        } />}
        <span className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">
          {label}
        </span>
      </div>
      <div className={
        "font-mono tabular-nums text-xl " +
        (tone === "pos" ? "text-pos" : tone === "neg" ? "text-neg" : "text-primary-fg")
      }>
        {value}{suffix && <span className="text-xs ml-0.5">{suffix}</span>}
      </div>
      {hint && (
        <div className="text-[10px] text-muted-fg mt-1">{hint}</div>
      )}
    </div>
  );
}

/** Inline SVG sparkline for the equity curve. */
function EquitySparkline({ points }) {
  if (!points || points.length < 2) {
    return (
      <div className="text-[11px] text-muted-fg italic text-center py-6">
        Not enough closed trades yet.
      </div>
    );
  }
  const W = 600;
  const H = 110;
  const PAD = 4;
  const ys = points.map((p) => p.equity);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const range = maxY - minY || 1;
  const stepX = (W - PAD * 2) / (points.length - 1);
  const toY = (v) => PAD + (1 - (v - minY) / range) * (H - PAD * 2);
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${PAD + i * stepX} ${toY(p.equity)}`)
    .join(" ");
  const area = `${path} L ${PAD + (points.length - 1) * stepX} ${H - PAD} L ${PAD} ${H - PAD} Z`;
  const finalUp = ys[ys.length - 1] >= 0;
  const tone = finalUp ? "#10b981" : "#ef4444";
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-28" preserveAspectRatio="none">
      <defs>
        <linearGradient id="eq-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={tone} stopOpacity="0.25" />
          <stop offset="100%" stopColor={tone} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#eq-grad)" />
      <path d={path} stroke={tone} strokeWidth="1.5" fill="none" />
      <line
        x1={PAD} x2={W - PAD}
        y1={toY(0)} y2={toY(0)}
        stroke="rgba(148,163,184,0.25)" strokeDasharray="3 3"
      />
    </svg>
  );
}

/** Horizontal bar chart — Scanner vs NIFTY 50 %. */
function ScannerVsNifty({ scanner, nifty }) {
  const maxAbs = Math.max(Math.abs(scanner), Math.abs(nifty), 5);
  const pct = (v) => Math.min(95, (Math.abs(v) / maxAbs) * 95);
  const row = (label, v, accent) => {
    const w = pct(v);
    const up = v >= 0;
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest">
          <span className="text-muted-fg">{label}</span>
          <span className={up ? "text-pos" : "text-neg"}>
            {up ? "+" : ""}{v.toFixed(2)}%
          </span>
        </div>
        <div className="h-2 bg-surface rounded-sm overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${w}%` }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className={"h-full " + (up ? accent : "bg-neg/70")}
          />
        </div>
      </div>
    );
  };
  return (
    <div className="space-y-3">
      {row("Breakout Scanner", scanner, "bg-pos")}
      {row("NIFTY 50", nifty, "bg-mod")}
    </div>
  );
}

const PERIODS = [
  { k: 30,  label: "30D" },
  { k: 90,  label: "3M" },
  { k: 183, label: "6M" },
  { k: 365, label: "1Y" },
];

export default function PerformanceDashboard({ compact = false, anonymous = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(365);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data: d } = await api.get("/public/performance", {
          params: { window_days: period },
        });
        if (!cancelled) setData(d);
      } catch {
        /* silent */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [period]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-fg gap-2">
        <CircleNotch size={16} className="animate-spin" />
        <span className="text-xs font-mono uppercase tracking-widest">Loading track record…</span>
      </div>
    );
  }

  if (!data) return null;
  const kpi = data.kpi || {};
  const risk = data.risk || {};
  const versus = data.scanner_vs_nifty || {};
  const outperf = versus.outperformance_pct ?? 0;
  const periodLabel =
    period === 30 ? "last 30 days" :
    period === 90 ? "last 3 months" :
    period === 183 ? "last 6 months" :
    "last 1 year";

  return (
    <section
      className={"border border-subtle rounded-sm bg-surface/30 " + (compact ? "p-4" : "p-5 sm:p-6")}
      data-testid="performance-dashboard"
    >
      <div className="flex items-start gap-2 flex-wrap mb-4">
        <ChartLineUp size={18} weight="fill" className="text-pos mt-0.5" />
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm font-medium text-primary-fg flex items-center gap-2">
            Live Track Record · <span className="text-muted-fg">{periodLabel}</span>
            {loading && <CircleNotch size={11} className="animate-spin text-muted-fg" />}
          </div>
          <div className="text-[11px] text-muted-fg">
            {anonymous
              ? "These are real closed signals from the scanner — not back-test simulations."
              : "Performance metrics across all closed signals in the selected window."}
          </div>
        </div>
        {/* Period toggle — segmented control style for clear visibility */}
        <div
          className="flex items-center gap-0 shrink-0 border border-pos/30 rounded-sm bg-surface/80 p-0.5"
          data-testid="perf-period-toggle"
        >
          <span className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            Period
          </span>
          {PERIODS.map((p) => (
            <button
              key={p.k}
              type="button"
              onClick={() => setPeriod(p.k)}
              data-testid={`perf-period-${p.k}`}
              className={
                "px-3 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm transition " +
                (period === p.k
                  ? "bg-pos text-black shadow-sm"
                  : "text-secondary-fg hover:text-pos hover:bg-pos-soft/30")
              }
            >
              {p.label}
            </button>
          ))}
        </div>
        <span className={
          "px-2 py-1 text-[10px] font-mono uppercase tracking-widest border rounded-sm shrink-0 " +
          (outperf >= 0 ? "border-pos text-pos bg-pos-soft/30" : "border-neg text-neg bg-neg-soft/20")
        }>
          {outperf >= 0 ? "+" : ""}{outperf.toFixed(2)}% vs NIFTY
        </span>
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          data-testid="perf-share-btn"
          aria-label="Share track record"
          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-pos/40 text-pos rounded-sm hover:bg-pos-soft/20 shrink-0 transition"
        >
          <ShareNetwork size={11} weight="bold" />
          Share
        </button>
      </div>

      <ShareCardModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title="Share live track record"
        shareText={`📈 Our scanner's track record: ${(versus.scanner_return_pct ?? 0).toFixed(2)}% vs NIFTY ${(versus.nifty_return_pct ?? 0).toFixed(2)}% (${periodLabel}). Real closed signals — not backtests.`}
        shareUrl="https://breakoutscanner.co.in/wins"
        filename={`track-record-${period}d.png`}
      >
        <ShareTrackRecordCard
          periodLabel={"Live Track Record · " + periodLabel}
          closedSignals={kpi.closed_signals ?? 0}
          winRate={kpi.win_rate ?? 0}
          profitFactor={kpi.profit_factor ?? 0}
          scannerReturn={versus.scanner_return_pct ?? 0}
          niftyReturn={versus.nifty_return_pct ?? 0}
          avgWin={risk.avg_win_pct ?? 0}
          avgLoss={risk.avg_loss_pct ?? 0}
        />
      </ShareCardModal>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mb-5">
        <Kpi
          label="Closed Signals" value={kpi.total_closed ?? 0}
          icon={Target} tone="neutral"
          testid="kpi-closed"
        />
        <Kpi
          label="Win Rate" value={`${(kpi.win_rate ?? 0).toFixed(1)}`} suffix="%"
          icon={TrendUp} tone={kpi.win_rate >= 50 ? "pos" : "neutral"}
          testid="kpi-winrate"
        />
        <Kpi
          label="Avg P&L / Trade" value={`${(kpi.avg_pnl_pct ?? 0) >= 0 ? "+" : ""}${(kpi.avg_pnl_pct ?? 0).toFixed(2)}`} suffix="%"
          icon={Coins} tone={kpi.avg_pnl_pct >= 0 ? "pos" : "neg"}
          testid="kpi-avgpnl"
        />
        <Kpi
          label="Profit Factor" value={(kpi.profit_factor ?? 0).toFixed(2)}
          icon={Pulse} tone={kpi.profit_factor >= 1.5 ? "pos" : kpi.profit_factor >= 1 ? "neutral" : "neg"}
          hint={kpi.profit_factor >= 1.5 ? "Excellent" : kpi.profit_factor >= 1 ? "Profitable" : "Breakeven-"}
          testid="kpi-profit-factor"
        />
        <Kpi
          label="Expectancy / Trade" value={`${(kpi.expectancy_pct ?? 0) >= 0 ? "+" : ""}${(kpi.expectancy_pct ?? 0).toFixed(2)}`} suffix="%"
          icon={ChartLineUp} tone={kpi.expectancy_pct >= 0 ? "pos" : "neg"}
          testid="kpi-expectancy"
        />
      </div>

      {/* Bottom row: equity curve · NIFTY vs scanner · risk mgmt */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Equity curve */}
        <div className="border border-subtle rounded-sm p-3 bg-surface/40 lg:col-span-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-2 flex items-center gap-1.5">
            <ChartLineUp size={11} weight="fill" className="text-pos" />
            Cumulative Equity (% summed)
          </div>
          <EquitySparkline points={data.equity_curve || []} />
          <div className="text-[10px] text-muted-fg mt-1">
            Compounds linearly across all closed trades · y=0 is breakeven.
          </div>
        </div>

        {/* Scanner vs NIFTY */}
        <div className="border border-subtle rounded-sm p-3 bg-surface/40 lg:col-span-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-3 flex items-center gap-1.5">
            <ArrowsHorizontal size={11} weight="fill" className="text-mod" />
            Scanner vs NIFTY 50 (% return)
          </div>
          <ScannerVsNifty
            scanner={versus.scanner_return_pct ?? 0}
            nifty={versus.nifty_return_pct ?? 0}
          />
          <div className="text-[10px] text-muted-fg mt-3">
            Scanner = cumulative % across all closed signals. NIFTY = index spot return.
          </div>
        </div>

        {/* Risk management */}
        <div className="border border-subtle rounded-sm p-3 bg-surface/40 lg:col-span-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-3 flex items-center gap-1.5">
            <ShieldCheck size={11} weight="fill" className="text-info" />
            Risk Management
          </div>
          <div className="space-y-2 text-[11px]">
            <div className="flex items-center justify-between">
              <span className="text-muted-fg flex items-center gap-1">
                <Clock size={10} /> Avg Hold Time
              </span>
              <span className="font-mono tabular-nums text-primary-fg">
                {(risk.avg_hold_days ?? 0).toFixed(1)}d
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-fg">Avg Win</span>
              <span className="font-mono tabular-nums text-pos">
                +{(risk.avg_win_pct ?? 0).toFixed(2)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-fg">Avg Loss</span>
              <span className="font-mono tabular-nums text-neg">
                {(risk.avg_loss_pct ?? 0).toFixed(2)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-fg">Risk : Reward</span>
              <span className="font-mono tabular-nums text-primary-fg">
                1 : {(risk.risk_reward ?? 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
